import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import JsonLd from '@/components/feature/JsonLd';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

const BASE_URL = 'https://mitao-video.example.com';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const article = id ? articles[id] : null;

  if (!article) {
    return (
      <main className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
            <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-semibold text-foreground-950 mb-2">视频内容未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">该视频内容可能已被移除或链接无效</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === article.category);
  const relatedVideos = article.relatedArticles
    .map((rid) => articles[rid])
    .filter(Boolean);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: BASE_URL },
      {
        '@type': 'ListItem',
        position: 2,
        name: `${categoryInfo?.name || article.category}视频内容`,
        item: `${BASE_URL}/category/${article.category}`,
      },
      { '@type': 'ListItem', position: 3, name: article.title, item: `${BASE_URL}/article/${article.id}` },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.thumbnail,
    datePublished: article.date,
    dateModified: article.date,
    author: { '@type': 'Person', name: article.author },
    publisher: {
      '@type': 'Organization',
      name: '蜜桃视频在线',
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${BASE_URL}/article/${article.id}` },
  };

  return (
    <>
      <JsonLd data={breadcrumbSchema} />
      <JsonLd data={articleSchema} />
      <SEOMeta
        title={article.title}
        description={article.description}
        keywords={`蜜桃视频在线,${article.tags.join(',')},${article.title},蜜桃视频在线观看`}
        canonicalUrl={`${BASE_URL}/article/${article.id}`}
        ogImage={article.thumbnail}
        ogType="article"
        articlePublishedTime={article.date}
        articleModifiedTime={article.date}
      />

      <main className="min-h-screen pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: categoryInfo?.name || article.category, to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <header className="mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 font-heading mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views} 次观看
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.duration}
              </span>
            </div>
          </header>

          <div className="relative w-full aspect-video rounded-xl overflow-hidden mb-8">
            <img
              src={article.thumbnail}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div
            className="prose-custom mb-10 text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap gap-2 mb-10">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
              >
                #{tag}
              </span>
            ))}
          </div>

          {relatedVideos.length > 0 && (
            <section className="border-t border-background-200/70 pt-8">
              <h2 className="text-lg font-semibold text-foreground-950 font-heading mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {relatedVideos.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group flex gap-3 bg-background-100 rounded-xl p-3 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-28 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                      <img
                        src={rel.thumbnail}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xs font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">
                        {rel.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-1 mb-1.5">
                        {rel.description}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-foreground-400">
                        <span>{rel.views} 观看</span>
                        <span>{rel.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
    </>
  );
}